import apiClient from '@/shared/services/api/apiClient';

export const getTeamMembers = async (
  params: Record<string, any> = {}
) => {
  const response: any = await apiClient.get('/team', { params });
  return response.data;
};

export const getInterviewers = async () => {
  const response: any = await apiClient.get('/team/interviewers');
  return response.data;
};

export const createTeamMember = async (
  member: Record<string, any>
) => {
  const response: any = await apiClient.post(
    '/team/addTeamMember',
    member
  );
  return response.data;
};

export const reactivateTeamMember = async (
  memberId: string,
  payload: {
    name: string;
    password: string;
    requestedHrAdminId?: string;
  }
) => {
  const { requestedHrAdminId, name, password } = payload;
  const response: any = await apiClient.post(
    `/team/${memberId}/reactivate`,
    { name, password },
    requestedHrAdminId
      ? { params: { requestedHrAdminId } }
      : undefined
  );
  return response.data;
};

export interface ReactivationTransferRequest {
  id: string;
  teamMemberId: string;
  requestedById: string;
  requestedByRole: string;
  currentHrAdminId: string;
  requestedHrAdminId: string;
  proposedName: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewerNote?: string | null;
  createdAt: string;
  reviewedAt?: string | null;
  memberName: string;
  memberEmail: string;
  requestedByName: string;
  requestedByEmail: string;
  currentHrAdminName: string;
  requestedHrAdminName: string;
}

export interface ReactivationTransferRequestsResponse {
  incomingRequests: ReactivationTransferRequest[];
  myRequests: ReactivationTransferRequest[];
}


let reactivationTransferRequestsPromise: Promise<ReactivationTransferRequestsResponse> | null = null;

export const getReactivationTransferRequests =
  (): Promise<ReactivationTransferRequestsResponse> => {
    if (reactivationTransferRequestsPromise) {
      return reactivationTransferRequestsPromise;
    }

    const request = apiClient
      .get('/team/reactivation-transfer-requests')
      .then((response: any) => {
        return response.data?.data ?? response.data;
      });

    reactivationTransferRequestsPromise = request;

    void request.finally(() => {
      if (reactivationTransferRequestsPromise === request) {
        reactivationTransferRequestsPromise = null;
      }
    }).catch(() => {
      // The original promise still rejects for callers to handle.
    });

    return request;
  };


export const decideReactivationTransferRequest = async (
  requestId: string,
  decision: 'APPROVE' | 'REJECT',
  note?: string
) => {
  const response: any = await apiClient.patch(
    `/team/reactivation-transfer-requests/${requestId}/decision`,
    { decision, note }
  );
  return response.data?.data ?? response.data;
};

/** Active member ownership transfer; separate from reactivation requests. */
export interface ActiveTeamMemberTransferRequest {
  id: string;
  teamMemberId: string;
  requestedById: string;
  currentHrAdminId: string;
  requestedHrAdminId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewerNote?: string | null;
  createdAt: string;
  reviewedAt?: string | null;
  memberName: string;
  memberEmail?: string | null;
  requestedByName: string;
  requestedByEmail?: string | null;
  currentHrAdminName: string;
  requestedHrAdminName: string;
}

export interface ActiveTeamMemberTransferRequestsResponse {
  incomingRequests: ActiveTeamMemberTransferRequest[];
  myRequests: ActiveTeamMemberTransferRequest[];
}

export const getActiveTeamMemberTransferRequests = async (): Promise<ActiveTeamMemberTransferRequestsResponse> => {
  const response: any = await apiClient.get('/team/transfer-requests');
  return response.data?.data ?? response.data;
};

export const requestActiveTeamMemberTransfer = async (memberId: string) => {
  const response: any = await apiClient.post(
    `/team/${memberId}/transfer-requests`
  );
  return response.data?.data ?? response.data;
};

export const decideActiveTeamMemberTransferRequest = async (
  requestId: string,
  decision: 'APPROVE' | 'REJECT',
  note?: string
) => {
  const response: any = await apiClient.patch(
    `/team/transfer-requests/${requestId}/decision`,
    { decision, note }
  );
  return response.data?.data ?? response.data;
};

export const updateTeamMember = async (
  memberId: string,
  updates: Record<string, any>
) => {
  const response: any = await apiClient.patch(`/team/${memberId}`, updates);
  return response.data;
};

export const updateTeamMemberStatus = async (
  memberId: string,
  status: string
) => {
  const response: any = await apiClient.patch(
    `/team/${memberId}/status`,
    { status }
  );
  return response.data;
};

export const getJobsForTransfer = async (memberId: string) => {
  const response: any = await apiClient.get(
    `/team/${memberId}/jobs-for-transfer`
  );

  return response.data;
};

export type OwnershipAssignment = {
  jobId: string;
  assignedToId: string;
};

export const deleteTeamMember = async (
  memberId: string,
  ownershipAssignments: OwnershipAssignment[] = []
) => {
  const response: any = await apiClient.delete(
    `/team/${memberId}`,
    {
      data: {
        ownershipAssignments,
      },
    }
  );

  return response.data;
};
